import { Button, Col, Form, Row } from "react-bootstrap";
import { useCallback, useEffect, useState } from "react";
import DateTimePicker from 'react-datetime';

import { applyChangeIfDateValid, validIfSameDayOrAfter } from '../../../../utils/datepicker';
import { ChainedOfferEventStatus } from "../../../../__gqltypes__";
import { stringToEnum } from "../screens/SaleEvent/utils";

export type EventBaseDataConfig = {
  name: string;
  startDate: Date | null;
  stopDate: Date | null;
  status: ChainedOfferEventStatus;
}

export const eventBaseDataConfigDefault = {
  name: '',
  startDate: new Date(),
  stopDate: new Date(),
  status: ChainedOfferEventStatus.DRAFT,
};

type Props = {
  data: EventBaseDataConfig;
  eventId: string;
  canSave?: boolean;
  // Hide the start/stop pickers for events whose dates are derived elsewhere (e.g. Marathon groups)
  hideDates?: boolean;
  onShowDeleteModal: () => void;
  onSave: () => void;
  onUpdate: (newData: EventBaseDataConfig | ((prevState: EventBaseDataConfig) => EventBaseDataConfig)) => void;
}

export function EventBaseData({
  data,
  eventId,
  canSave = true,
  hideDates = false,
  onShowDeleteModal,
  onSave,
  onUpdate
}: Props): JSX.Element {
  const [name, setName] = useState('');
  const [status, setStatus] = useState(ChainedOfferEventStatus.DRAFT);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [stopDate, setStopDate] = useState<Date | null>(null);

   const handleChangeName = useCallback((event) => {
      const name_ = event.target.value;
      setName(name_);
      onUpdate((prev) => ({...prev, name: name_}));
    }, [onUpdate]);

  const handleChangeStatus = useCallback((event) => {
    const s = event.currentTarget.value as ChainedOfferEventStatus;
    setStatus(s);
    onUpdate((prev) => ({...prev, status: s}));
  }, [onUpdate]);

  const handleStartDate = useCallback((value: Date) => {
    setStartDate(value);
    onUpdate((prev) => ({...prev, startDate: value}));
  }, [onUpdate]);

  const handleStopDate = useCallback((value: Date) => {
    setStopDate(value);
    onUpdate((prev) => ({...prev, stopDate: value}));
  }, [onUpdate]);

  useEffect(() => {
    setName(data.name);
    setStatus(data.status);
    setStartDate(data.startDate);
    setStopDate(data.stopDate);
  }, [data]);


  return (
    <>
      <Row className="mb-3">
          <Col md={3}>Id:</Col>
          <Col md={5}>{eventId}</Col>
          <Col className="d-flex justify-content-end">
            <Button
              className="mr-2"
              onClick={onShowDeleteModal}
              variant="danger"
              disabled={false}
            >
              <i className="fas fa-trash mr-2" />
              Delete
            </Button>
            <Button onClick={onSave} variant="success" type="submit" disabled={!canSave}>
                Save
            </Button>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={3}>Name:</Col>
          <Col md={5}>
            <Form.Control value={name} onChange={handleChangeName} type="text" />
          </Col>
        </Row>

        {!hideDates && (
          <Row className="mb-3">
            <Col md={3}>Start date:</Col>
            <Col md={5}>
              <DateTimePicker
                dateFormat="YYYY-MM-DD"
                timeFormat={true}
                timeConstraints={{ minutes: { min: 0, max: 0, step: 0 } }}
                // @ts-ignore
                onChange={applyChangeIfDateValid(handleStartDate)}
                isValidDate={validIfSameDayOrAfter}
                // @ts-ignore
                value={startDate ?? undefined}
              />
            </Col>
          </Row>
        )}

        {!hideDates && (
          <Row className="mb-3">
            <Col md={3}>Stop date:</Col>
            <Col md={5}>
              <DateTimePicker
                dateFormat="YYYY-MM-DD"
                timeFormat={true}
                timeConstraints={{ minutes: { min: 0, max: 0, step: 0 } }}
                // @ts-ignore
                onChange={applyChangeIfDateValid(handleStopDate)}
                isValidDate={validIfSameDayOrAfter}
                // @ts-ignore
                value={stopDate ?? undefined}
              />
            </Col>
          </Row>
        )}

         <Row className="mb-3">
          <Col md={3}>Status:</Col>
          <Col md={2}>
            <Form.Group>
              <Form.Control as="select" type="text" value={status} onChange={handleChangeStatus}>
                {Object.keys(ChainedOfferEventStatus).map((option) => (
                  <option key={option} value={stringToEnum(option, ChainedOfferEventStatus)}>
                    {option}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>

    </>
  );
}